import React from 'react'

import { XIcon } from 'lucide-react'
import { action, makeObservable, observable } from 'mobx'
import { observer } from 'mobx-react'
import { isAndroid } from 'react-device-detect'

import { Button } from '~/client/src/shared/components/Button/Button'
import { LocalStorageKey } from '~/client/src/shared/enums/LocalStorageKey'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { preventLastWordWrap } from '~/client/src/shared/utils/textUtils'

import { androidStoreURL, iosStoreURL } from '../../constants/storesInfo'

import './GetTheAppBanner.scss'

interface IProps {
  hidden?: boolean
  onDismiss?: () => void
}

const ANIMATION_DURATION = 1000

/**
 * A banner that prompts the user to download the mobile app.
 * Dev tip: hold Ctrl to dismiss it for good ;)
 */
@observer
export default class GetTheAppBanner extends React.Component<IProps> {
  @observable private shouldRender: boolean

  public constructor(props: IProps) {
    super(props)
    this.shouldRender = !props.hidden
    makeObservable(this)
  }

  private get downloadUrl(): string {
    return isAndroid ? androidStoreURL : iosStoreURL
  }

  public componentDidUpdate(prevProps: Readonly<IProps>): void {
    if (this.props.hidden && !prevProps.hidden) {
      setTimeout(() => {
        this.disableRender()
      }, ANIMATION_DURATION)
    } else if (!this.props.hidden && prevProps.hidden) {
      this.enableRender()
    }
  }

  public render() {
    const i18n = Localization.translator
    const hiddenClass = this.props.hidden ? 'banner--hidden' : ''
    return this.shouldRender ? (
      <div className={`get-the-app-banner row ${hiddenClass}`}>
        <div className="close flex-shrink">
          <Button
            variant="ghost"
            size="icon"
            className="tw:text-white tw:hover:bg-white/10 tw:hover:text-white"
            aria-label={i18n.close}
            onClick={this.handleDismiss}
          >
            <XIcon />
          </Button>
        </div>
        <div className="body-text text large white">
          {preventLastWordWrap(i18n.getMobileNativeApp_long)}
        </div>
        <div className="buttons">
          <a
            className="bg-color-blue-brand brada24 button large text white"
            href={this.downloadUrl}
            target="_blank"
          >
            {i18n.download}
          </a>
        </div>
      </div>
    ) : null
  }

  private handleDismiss = (event: React.MouseEvent<HTMLButtonElement>) => {
    sessionStorage.setItem(LocalStorageKey.GetTheAppBannerDismissed, 'true')

    this.props.onDismiss()

    if (event.ctrlKey) {
      localStorage.setItem(LocalStorageKey.GetTheAppBannerDismissed, 'true')
    }
  }

  @action.bound
  private enableRender() {
    this.shouldRender = true
  }

  @action.bound
  private disableRender() {
    this.shouldRender = false
  }
}

export function shouldShowBanner(): boolean {
  return (
    // Not in the webview
    !window.ReactNativeWebView &&
    // One-time popup has already been dismissed
    !!localStorage.getItem(LocalStorageKey.OneTimePopup) &&
    // Banner hasn't been dismissed yet
    !isBannerDismissed()
  )
}

function isBannerDismissed(): boolean {
  return (
    !!localStorage.getItem(LocalStorageKey.GetTheAppBannerDismissed) ||
    !!sessionStorage.getItem(LocalStorageKey.GetTheAppBannerDismissed)
  )
}
